import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { format, isSameDay } from 'date-fns';
import { uniq } from 'lodash';
import { useLazyQuery } from '@apollo/client';
import {
  IconBullet,
  IconEdit,
  IconRepeat,
  IconWrapper,
  SubmitButton,
  IconError,
  IconAdd,
  IconDelete,
  SelectOptionType,
} from '../../../../../../../../components';
import Calendar from '../../../../../../../../components/calendar';
import { useInstallationContext } from '../../../../../../../../context/installation';
import {
  HolidaysHandling,
  PriceTriggerSubType,
  Query,
  QueryCalculateFutureFireTimesArgs,
} from '../../../../../../../../data-access/gql-types/graphql';
import { CALCULATE_FUTURE_FIRE_TIMES } from '../../../../../../../../data-access/queries/triggers';
import { getEndTimeOfDay, getFormatInTimeZone } from '../../../../../../../../utils/helpers';
import { TriggerPriceConditionType } from '../../../../../../types';
import { prepareExecution } from '../../../../../trigger-details/utils';
import { useTriggerFormContext } from '../../../../context/provider';
import { useOptions } from '../../../../hooks/price-condition/use-options';
import { usePickers } from '../../../../hooks/price-condition/use-pickers';
import { RepeatType } from '../../../time-condition/enums/repeat-type.enum';
import { TimeEndType } from '../../../time-condition/enums/time-end-type.enum';
import { PriceConditionType } from '../../enums';
import { PriceConditionPickers } from '../pickers';

export type DynamicSubTypeProps = {
  goToChannels?: () => void;
  goToSummary?: () => void;
};

const PriceConditionForm: React.FC<DynamicSubTypeProps> = ({ goToChannels, goToSummary }) => {
  const { t } = useTranslation('action');
  const { t: tc } = useTranslation('common');
  const { setPriceCondition, priceConditionConfig, actions, setPriceConditionConfigValue } = useTriggerFormContext();
  const [internalPriceCondition, setInternalPriceCondition] = useState<TriggerPriceConditionType>({});
  const { handleSubmit } = useForm();
  const { selectedInstallation } = useInstallationContext();
  const pickers = usePickers();
  const {
    timeEndPicker,
    repeatPicker,
    weekDaysPicker,
    fromHourPicker,
    priceTypeConditionPicker,
    lesserPicker,
    greaterPicker,
    cheapestCountPicker,
  } = pickers;
  const now = new Date();
  const [futureFireTimes, setFutureFireTimes] = useState<string[]>([]);
  const [isValid, setIsValid] = useState<boolean>(false);
  const { repeatOptions, timeEndType, repeatSelected, selectedDaysLabel, setRepeatSelected, setTimeEndType } =
    useOptions();

  const [calculateFutureFireTimes, { loading: calculateFutureFireLoading }] = useLazyQuery<
    Query,
    QueryCalculateFutureFireTimesArgs
  >(CALCULATE_FUTURE_FIRE_TIMES, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data.calculateFutureFireTimes?.futureFires) {
        const futureExecutions = data.calculateFutureFireTimes.futureFires.map((x) =>
          prepareExecution(x, selectedInstallation?.payload?.timeZone || ''),
        );

        setIsValid(true);
        setFutureFireTimes(uniq(futureExecutions));
      } else {
        setIsValid(false);
        setFutureFireTimes([]);
      }
    },
    onError: () => {
      setIsValid(false);
      setFutureFireTimes([]);
    },
  });

  useEffect(() => {
    const closeRepeatPicker = !repeatPicker.open && !weekDaysPicker.open;

    if (closeRepeatPicker) {
      if (!priceConditionConfig.selectedDays.length && repeatSelected.value === RepeatType.SELECTED) {
        setRepeatSelected(repeatOptions.find((option) => option.value === RepeatType.DAILY) ?? repeatOptions[0]);
      }
    }
  }, [repeatPicker.open]);

  const priceConditionOptions: SelectOptionType<PriceConditionType>[] = [
    { value: PriceConditionType.HOURS_FROM_TO, label: t('trigger.priceConditionOptions.hours') },
    { value: PriceConditionType.NUMBER_OF_CHEAPEST_PERIODS, label: t('trigger.priceConditionOptions.cheapestPeriods') },
    { value: PriceConditionType.PRICE_GREATER_OR_EQUAL, label: t('trigger.priceConditionOptions.priceGreater') },
    { value: PriceConditionType.PRICE_LESSER_OR_EQUAL, label: t('trigger.priceConditionOptions.priceLesser') },
  ];

  const priceConditionTypeChange = (condition: PriceConditionType) => {
    const foundCondition = priceConditionConfig.priceConditionTypes.find((x) => x === condition);
    if (!foundCondition)
      setPriceConditionConfigValue('priceConditionTypes', [...priceConditionConfig.priceConditionTypes, condition]);

    priceTypeConditionPicker.set(null);

    switch (condition) {
      case PriceConditionType.NUMBER_OF_CHEAPEST_PERIODS: {
        cheapestCountPicker.set(true);
        break;
      }
      case PriceConditionType.PRICE_GREATER_OR_EQUAL: {
        greaterPicker.set(true);
        break;
      }
      case PriceConditionType.PRICE_LESSER_OR_EQUAL: {
        lesserPicker.set(true);
        break;
      }
      default: {
        fromHourPicker.set(true);
      }
    }
  };

  const handleConditionDelete = (condition: PriceConditionType) => {
    const conditionIndex = priceConditionConfig.priceConditionTypes.findIndex((x) => x === condition);
    if (conditionIndex !== -1) {
      setPriceConditionConfigValue(
        'priceConditionTypes',
        [...priceConditionConfig.priceConditionTypes].filter((_, index) => index !== conditionIndex),
      );
    }
  };

  useEffect(() => {
    if (priceConditionConfig.subType === PriceTriggerSubType.Unknown || !selectedInstallation?.installationId) return;

    setIsValid(false);
    const isToday = isSameDay(Date.now(), priceConditionConfig.startDate);
    const dateEnd = format(priceConditionConfig.endDate, 'yyyy-MM-dd');
    const timeRepeatAsDate = new Date();
    timeRepeatAsDate.setHours(Number(priceConditionConfig.timeRepeat.split(':')[0]));
    timeRepeatAsDate.setMinutes(Number(priceConditionConfig.timeRepeat.split(':')[1]));

    const variables = {
      subType: priceConditionConfig.subType,
      holidaysHandling: priceConditionConfig.holidaysHandling,
      startAt: {
        date: getFormatInTimeZone(priceConditionConfig.startDate, 'UTC', 'yyyy-MM-dd'),
        ...(isToday
          ? { time: getFormatInTimeZone(new Date(), 'UTC', "'PT'HH'H'mm'M'") }
          : { time: getFormatInTimeZone(timeRepeatAsDate, 'UTC', "'PT'HH'H'mm'M'") }),
      },
      ...(timeEndType.value === TimeEndType.DATE && {
        endAt: {
          date: dateEnd,
          time: getFormatInTimeZone(getEndTimeOfDay(), 'UTC', "'PT'HH'H'mm'M'"),
        },
      }),
      ...((timeEndType.value === TimeEndType.COUNT || repeatSelected.value === RepeatType.ONCE) && {
        targetExecutionCount: priceConditionConfig.repeatCount,
      }),
      daysOfWeek: priceConditionConfig.selectedDays,
      ...(priceConditionConfig.priceConditionTypes.find((x) => x === PriceConditionType.HOURS_FROM_TO)
        ? {
            betweenHoursFromInclusive: parseInt(priceConditionConfig.betweenHoursFromInclusive.split(':')[0]),
            betweenHoursToInclusive: parseInt(priceConditionConfig.betweenHoursToInclusive.split(':')[0]),
          }
        : {}),
      ...(priceConditionConfig.priceConditionTypes.find((x) => x === PriceConditionType.NUMBER_OF_CHEAPEST_PERIODS)
        ? { numberOfCheapestPeriodsInDay: priceConditionConfig.numberOfCheapestPeriodsInDay }
        : {}),
      ...(priceConditionConfig.priceConditionTypes.find((x) => x === PriceConditionType.PRICE_GREATER_OR_EQUAL)
        ? { priceGreaterThanOrEqual: priceConditionConfig.priceGreaterThanOrEqual }
        : {}),
      ...(priceConditionConfig.priceConditionTypes.find((x) => x === PriceConditionType.PRICE_LESSER_OR_EQUAL)
        ? { priceLesserThanOrEqual: priceConditionConfig.priceLesserThanOrEqual }
        : {}),
    };

    setInternalPriceCondition(variables);

    calculateFutureFireTimes({
      variables: {
        trigger: {
          ...variables,
        },
        installationId: selectedInstallation?.installationId,
      },
    });
  }, [
    priceConditionConfig.repeatCount,
    priceConditionConfig.timeRepeat,
    priceConditionConfig.selectedDays,
    priceConditionConfig.startDate.getDate(),
    priceConditionConfig.endDate.getDate(),
    priceConditionConfig.priceConditionTypes,
    priceConditionConfig.betweenHoursFromInclusive,
    priceConditionConfig.betweenHoursToInclusive,
    priceConditionConfig.numberOfCheapestPeriodsInDay,
    priceConditionConfig.priceGreaterThanOrEqual,
    priceConditionConfig.priceLesserThanOrEqual,
    priceConditionConfig.subType,
    selectedInstallation?.installationId,
  ]);

  const renderOptionDetails = (val: PriceConditionType) => {
    switch (val) {
      case PriceConditionType.HOURS_FROM_TO:
        return priceConditionConfig.betweenHoursFromInclusive != null &&
          priceConditionConfig.betweenHoursToInclusive != null ? (
          <span>
            : {`${priceConditionConfig.betweenHoursFromInclusive}-${priceConditionConfig.betweenHoursToInclusive}`}
          </span>
        ) : null;
      case PriceConditionType.NUMBER_OF_CHEAPEST_PERIODS: {
        return priceConditionConfig.numberOfCheapestPeriodsInDay ? (
          <span>: {priceConditionConfig.numberOfCheapestPeriodsInDay}</span>
        ) : null;
      }
      case PriceConditionType.PRICE_GREATER_OR_EQUAL:
        return priceConditionConfig.priceGreaterThanOrEqual != null ? (
          <span>: {priceConditionConfig.priceGreaterThanOrEqual} zł/kWh</span>
        ) : null;
      case PriceConditionType.PRICE_LESSER_OR_EQUAL:
        return priceConditionConfig.priceLesserThanOrEqual != null ? (
          <span>: {priceConditionConfig.priceLesserThanOrEqual} zł/kWh</span>
        ) : null;
      default:
        return null;
    }
  };

  const onSubmit = () => {
    setPriceCondition(internalPriceCondition);

    if (goToChannels && goToSummary) {
      if (!actions.length) goToChannels();
      else goToSummary();
    }
  };

  const selectedPriceConditionsOptions = useMemo(() => {
    return priceConditionOptions.filter((x) => priceConditionConfig.priceConditionTypes.find((c) => x.value === c));
  }, [priceConditionOptions, priceConditionConfig.priceConditionTypes]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="max-width-desktop">
        <div className="condition-section condition-section--repeat ">
          <div className="condition-repeat" onClick={() => repeatPicker.set(true)}>
            <div>
              <IconWrapper className="m-r-16">
                <IconRepeat />
              </IconWrapper>
              <p>
                {repeatSelected.value !== RepeatType.SELECTED ? repeatSelected.label : selectedDaysLabel.split(': ')[1]}
              </p>
            </div>
            <IconEdit />
          </div>
          <div className="condition-repeat-box condition-repeat-box--relative">
            <Calendar
              covered
              date={priceConditionConfig.startDate}
              label={''}
              onChange={(date) => {
                date.setHours(Number(priceConditionConfig.timeRepeat.split(':')[0]));
                date.setMinutes(Number(priceConditionConfig.timeRepeat.split(':')[1]));
                setPriceConditionConfigValue('startDate', date);
              }}
            />
            <span className="condition-repeat-box__heading">{t('trigger.conditions.start')}</span>
            <span className="condition-repeat-box__date">
              {isSameDay(now, priceConditionConfig.startDate) ? `${t('trigger.conditions.today')}, ` : null}
              {format(priceConditionConfig.startDate, 'dd.MM.yyyy')}
            </span>
          </div>
          <div
            className={c('condition-repeat-box', {
              'condition-repeat-box--disabled': repeatSelected.value === RepeatType.ONCE,
            })}
            onClick={() => timeEndPicker.set(true)}
          >
            <span className="condition-repeat-box__heading">{t('trigger.conditions.end')}</span>
            <span className="condition-repeat-box__date">
              {timeEndType.value === TimeEndType.NEVER ? (
                t('trigger.timeEndOptions.never')
              ) : timeEndType.value === TimeEndType.COUNT ? (
                `${t('trigger.timeEndOptions.count')}: ${priceConditionConfig.repeatCount}`
              ) : (
                <>
                  {isSameDay(now, priceConditionConfig.endDate) ? `${t('trigger.conditions.today')}, ` : null}
                  {format(priceConditionConfig.endDate, 'dd.MM.yyyy')}
                </>
              )}
            </span>
          </div>
          {selectedPriceConditionsOptions.map((condition) => (
            <div key={condition.value} className="condition-repeat-box-container">
              <div className={c('condition-repeat-box')} onClick={() => priceConditionTypeChange(condition.value)}>
                <span className="condition-repeat-box__heading">{t('trigger.conditions.priceCondition')}</span>
                <span className="condition-repeat-box__date">
                  {condition.label}
                  {renderOptionDetails(condition.value)}
                </span>
              </div>
              <button className="btn-delete" type="button" onClick={() => handleConditionDelete(condition.value)}>
                <IconDelete />
              </button>
            </div>
          ))}
          {priceConditionConfig.priceConditionTypes.length < 4 ? (
            <button className="condition-add" onClick={() => priceTypeConditionPicker.set(true)} type="button">
              <IconAdd />
              <p>{t('trigger.addCondition')}</p>
            </button>
          ) : null}
          {calculateFutureFireLoading ? (
            <div className="condition-repeat__next-execution">
              <IconBullet />
              <p>{t('trigger.nextFireTimeLoading')}</p>
            </div>
          ) : isValid ? (
            <>
              <div className="condition-repeat__next-execution">
                <IconBullet />
                <p>{`${t('trigger.nextFireTime')}: ${!futureFireTimes.length ? t('trigger.nextFireTimeNull') : ''}`}</p>
              </div>
              {futureFireTimes.map((x) => (
                <div key={x} className="condition-repeat__next-execution p-l-24">
                  <p>{x}</p>
                </div>
              ))}
            </>
          ) : (
            <div className="condition-repeat__next-execution condition-repeat__next-execution--error">
              <IconError />
              <p>{t('trigger.nextFireTimeError')}</p>
            </div>
          )}
        </div>

        <SubmitButton disabled={!isValid}>{tc('buttons.next')}</SubmitButton>
      </form>

      <PriceConditionPickers
        pickers={pickers}
        priceConditionOptions={priceConditionOptions}
        priceConditionTypes={priceConditionConfig.priceConditionTypes}
        priceConditionTypeChange={priceConditionTypeChange}
        repeatSelected={repeatSelected}
        setRepeatSelected={setRepeatSelected}
        timeEndType={timeEndType}
        setTimeEndType={setTimeEndType}
      />
    </>
  );
};

export default PriceConditionForm;
